<script lang="ts" setup>
import getWidgetTextTranslation from '~/utils/getWidgetTextTranslation'
import buildCategoryUrl from '~/utils/filters/buildCategoryUrl'
import type { TranslatableString } from '~/types/widgets/widgetTypes'
import WidgetLayout from './WidgetLayout.vue'

import DealzoneLogo from '~/assets/images/Dealzone-logo.svg'
import QuotesLogo from 'assets/images/quotes-logo.svg'
import ShipzeeLogo from '~/assets/images/Shipzee-logo.svg'
import ShipzeeProLogo from '~/assets/images/ShipzeePro-logo.svg'

type LogoOptions = 'Dealzone' | 'Quotes' | 'Shipzee' | 'SitePro'
type LinkOptions = 'Button' | 'Title'
type HeadingOptions = 'H1' | 'H2'

const { title, description, descriptionTextColor, slug, linkText, linkType, backgroundColor, logo, headingType } =
  defineProps<{
    title: TranslatableString
    description?: TranslatableString
    descriptionTextColor?: string
    slug?: string
    linkText?: TranslatableString
    linkType?: LinkOptions | Array<LinkOptions>
    logo?: LogoOptions
    backgroundColor?: string
    headingType?: HeadingOptions | Array<HeadingOptions>
  }>()

const selectedLogo: Record<LogoOptions, string> = {
  Dealzone: DealzoneLogo,
  Quotes: QuotesLogo,
  Shipzee: ShipzeeLogo,
  SitePro: ShipzeeProLogo,
}

const selectedType = computed(() => {
  const type = typeof linkType === 'string' ? linkType : linkType?.[0]

  switch (type) {
    case 'Button':
    case 'Title':
      return type
    default:
      return 'Button'
  }
})

const selectedDescriptionTextColor = computed(() => {
  return descriptionTextColor || '#212121'
})

const titleHeadingType = computed(() => {
  if (typeof headingType === 'string') {
    return headingType === 'H1' ? 'h1' : 'h2'
  }

  return headingType?.[0] === 'H1' ? 'h1' : 'h2'
})
</script>

<template>
  <WidgetLayout :background-color="backgroundColor" class-name="!pb-0">
    <div class="widget-header flex flex-col items-start justify-between gap-4">
      <template v-if="selectedType === 'Title' && slug">
        <NuxtLink class="flex flex-row flex-1 gap-1" :to="buildCategoryUrl(slug, true)">
          <div v-if="logo && selectedLogo[logo]">
            <img v-if="logo" :src="selectedLogo[logo]" class="logo" alt="Logo" loading="lazy" />
          </div>
          <div class="flex items-center">
            <component
              :is="titleHeadingType"
              class="h5 text-text1 dark:text-dmText1"
              v-text="getWidgetTextTranslation(title)" />
          </div>
          <div class="flex items-center">
            <span class="h5 text-text1 dark:text-dmText1 pl-2">
              <i class="fas fa-arrow-right" />
            </span>
          </div>
        </NuxtLink>
      </template>

      <template v-else>
        <div class="flex flex-row flex-1 gap-1">
          <div v-if="logo && selectedLogo[logo]">
            <img v-if="logo" :src="selectedLogo[logo]" class="logo" alt="Logo" />
          </div>
          <div class="flex items-center">
            <component
              :is="titleHeadingType"
              class="h5 text-text1 dark:text-dmText1"
              v-text="getWidgetTextTranslation(title)" />
          </div>
        </div>
      </template>

      <div
        v-if="description"
        class="text-base lg:leading-tight lg:text-lg mt-2 w-full px-6 lg:px-4 dark:!text-dmText1"
        :style="{ color: selectedDescriptionTextColor }"
        v-html="getWidgetTextTranslation(description)" />

      <template v-if="selectedType === 'Button' && slug">
        <NuxtLink class="btn-secondary !p-4 lg:!px-10" :to="buildCategoryUrl(slug, true)">
          <span v-if="linkText" class="hidden lg:block"> {{ getWidgetTextTranslation(linkText) }} </span>
          <i class="fas fa-arrow-right" />
        </NuxtLink>
      </template>
    </div>
  </WidgetLayout>
</template>

<style lang="postcss" scoped>
.widget-header {
  .logo {
    @apply bg-transparent dark:bg-white rounded-[4px];

    height: 2.5rem;
    width: auto;
    max-width: 100px;
    margin-right: 10px;
  }

  .btn-secondary {
    i {
      font-size: 1.25rem;
    }

    span {
      padding-right: 0.75rem;
    }
  }
}

.dark .widget-section {
  @apply !bg-transparent;
}
</style>
